import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Users, Bell, BellOff, Bot, RefreshCw, MessageSquare, ArrowLeft, ExternalLink, Settings
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function WhatsAppGroups({ session, onBack }) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const extractActionLabel = groups.length > 0 ? t('groups.reextract') : t('groups.extract');
  const [groupSettings, setGroupSettings] = useState({
    isMuted: true,
    notifyToGhl: false,
    aiOff: true,
  });

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/whatsapp/sessions/${session.sessionId}/groups`);
      const data = response.data;
      setGroups(data.groups || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error(t('groups.toastLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [session.sessionId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleExtractGroups = async () => {
    try {
      setExtracting(true);
      toast.info(t('groups.toastExtracting'));
      
      const response = await api.post(`/whatsapp/sessions/${session.sessionId}/groups/extract`);
      const data = response.data;
      
      if (data.success) {
        toast.success(t('groups.toastExtracted', { count: data.count }));
        fetchGroups();
      } else {
        toast.error(data.error || t('groups.toastExtractFailed'));
      }
    } catch (error) {
      console.error('Error extracting groups:', error);
      toast.error(t('groups.toastExtractFailed'));
    } finally {
      setExtracting(false);
    }
  };

  const handleOpenSettings = (group) => {
    setSelectedGroup(group);
    setGroupSettings({
      isMuted: group.isMuted !== false, // Default true (silenciado)
      notifyToGhl: group.notifyToGhl || false,
      aiOff: group.aiOff !== false // Default true (AI OFF, no responde)
    });
    setShowSettingsModal(true);
  };

  const handleSaveSettings = async () => {
    try {
      const response = await api.put(
        `/whatsapp/sessions/${session.sessionId}/groups/${encodeURIComponent(selectedGroup.groupId)}`,
        groupSettings
      );
      const data = response.data;
      
      if (data.success) {
        toast.success(t('groups.toastSaved'));
        setShowSettingsModal(false);
        fetchGroups();
      } else {
        toast.error(data.error || t('groups.toastSaveFailed'));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(t('groups.toastSaveFailed'));
    }
  };

  const handleBulkMute = async (mute = true) => {
    try {
      const groupIds = groups.map(g => g.groupId);
      
      const response = await api.post(`/whatsapp/sessions/${session.sessionId}/groups/bulk-mute`, { groupIds, isMuted: mute });
      const data = response.data;
      
      if (data.success) {
        toast.success(mute ? t('groups.toastAllMuted') : t('groups.toastAllUnmuted'));
        fetchGroups();
      } else {
        toast.error(data.error || 'Error');
      }
    } catch (error) {
      console.error('Error bulk mute:', error);
      toast.error(t('groups.toastUpdateFailed'));
    }
  };

  const handleActivateGroup = async (group) => {
    try {
      const response = await api.post(
        `/whatsapp/sessions/${session.sessionId}/groups/${encodeURIComponent(group.groupId)}/activate`,
        { sendNotification: true }
      );
      const data = response.data;
      
      if (data.success) {
        toast.success(t('groups.toastJoined', { name: group.name }));
        fetchGroups();
      } else {
        toast.error(data.error || t('groups.toastJoinFailed'));
      }
    } catch (error) {
      console.error('Error activating group:', error);
      toast.error(t('groups.toastJoinFailed'));
    }
  };

  const handleMuteGroup = async (group) => {
    try {
      const response = await api.post(
        `/whatsapp/sessions/${session.sessionId}/groups/${encodeURIComponent(group.groupId)}/mute`,
        { sendNotification: true }
      );
      const data = response.data;
      
      if (data.success) {
        toast.success(t('groups.toastLeft', { name: group.name }));
        fetchGroups();
      } else {
        toast.error(data.error || t('groups.toastLeaveFailed'));
      }
    } catch (error) {
      console.error('Error muting group:', error);
      toast.error(t('groups.toastLeaveFailed'));
    }
  };

  // Silenciar/Activar sonido: los mensajes SIGUEN llegando al CRM,
  // solo se marcan como leídos para que no notifiquen.
  const handleToggleSilent = async (group, silent) => {
    try {
      const response = await api.put(
        `/whatsapp/sessions/${session.sessionId}/groups/${encodeURIComponent(group.groupId)}`,
        { isMuted: silent }
      );
      const data = response.data;
      if (data.success) {
        toast.success(silent
          ? t('groups.toastMuted', { name: group.name })
          : t('groups.toastUnmuted', { name: group.name }));
        fetchGroups();
      } else {
        toast.error(data.error || t('groups.toastUpdateFailed'));
      }
    } catch (error) {
      console.error('Error toggling silent:', error);
      toast.error(t('groups.toastUpdateFailed'));
    }
  };

  const formatDate = (date) => {
    if (!date) return t('groups.noActivity');
    return new Date(date).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-groups">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('groups.title')}</h1>
            <p className="text-muted-foreground">{session.name} - {session.phoneNumber || t('groups.noNumber')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            data-testid="groups-bulk-mute-button"
            variant="outline"
            onClick={() => handleBulkMute(true)}
            disabled={groups.length === 0}
          >
            <BellOff className="w-4 h-4 mr-2" />
            {t('groups.muteAll')}
          </Button>
          <Button 
            data-testid="groups-extract-button"
            onClick={handleExtractGroups}
            disabled={extracting || session.status !== 'connected'}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${extracting ? 'animate-spin' : ''}`} />
            {extracting ? t('groups.extracting') : extractActionLabel}
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground" data-testid="groups-extract-legend">
        {t('groups.extractLegend')}
      </p>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <BellOff className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">{t('groups.bannerTitle')}</p>
              <p className="text-sm text-blue-700">
                {t('groups.bannerText')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('groups.totalGroups')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{groups.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('groups.mutedCount')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">
              {groups.filter(g => g.isMuted !== false).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('groups.soundCount')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {groups.filter(g => g.isMuted === false).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Groups List */}
      <Card>
        <CardHeader>
          <CardTitle>{t('groups.listTitle')}</CardTitle>
          <CardDescription>
            {t('groups.listSub')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('groups.loading')}
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">{t('groups.emptyTitle')}</h3>
              <p className="text-muted-foreground mb-4">
                {t('groups.emptySub')}
              </p>
              <Button 
                onClick={handleExtractGroups}
                disabled={extracting || session.status !== 'connected'}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${extracting ? 'animate-spin' : ''}`} />
                {t('groups.extract')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {groups.map((group) => (
                <div 
                  key={group.groupId}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  data-testid={`group-${group.groupId}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      group.notifyToGhl ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <Users className={`w-6 h-6 ${
                        group.notifyToGhl ? 'text-green-600' : 'text-gray-400'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{group.name}</span>
                        {group.notifyToGhl && (
                          group.isMuted === false ? (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              <Bell className="w-3 h-3 mr-1" />
                              {t('groups.badgeActiveSound')}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                              <BellOff className="w-3 h-3 mr-1" />
                              {t('groups.badgeActiveMuted')}
                            </Badge>
                          )
                        )}
                        {group.aiOff === false && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            <Bot className="w-3 h-3 mr-1" />
                            {t('groups.badgeAiOn')}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t('groups.participants', { count: group.participantCount || 0 })} • {t('groups.messages', { count: group.messageCount || 0 })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('groups.lastMessage')}: {formatDate(group.lastMessageAt)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!group.notifyToGhl ? (
                      <Button 
                        data-testid={`group-enter-${group.groupId}`}
                        size="sm"
                        onClick={() => handleActivateGroup(group)}
                      >
                        {t('groups.enter')}
                      </Button>
                    ) : (
                      <>
                        <Button
                          data-testid={`group-silent-${group.groupId}`}
                          variant={group.isMuted === false ? "outline" : "secondary"}
                          size="sm"
                          title={group.isMuted === false
                            ? t('groups.titleMute')
                            : t('groups.titleUnmute')}
                          onClick={() => handleToggleSilent(group, group.isMuted === false)}
                        >
                          {group.isMuted === false ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                        </Button>
                        <Button 
                          data-testid={`group-leave-${group.groupId}`}
                          variant="outline"
                          size="sm"
                          onClick={() => handleMuteGroup(group)}
                        >
                          {t('groups.leave')}
                        </Button>
                      </>
                    )}
                    <Button 
                      data-testid={`group-settings-${group.groupId}`}
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleOpenSettings(group)}
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Group Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('groups.settingsTitle')}</DialogTitle>
            <DialogDescription>
              {selectedGroup?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Muted Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">{t('groups.muteGroup')}</Label>
                <p className="text-sm text-muted-foreground">
                  {t('groups.muteGroupDesc')}
                </p>
              </div>
              <Switch
                checked={groupSettings.isMuted}
                onCheckedChange={(checked) => setGroupSettings({ 
                  ...groupSettings, 
                  isMuted: checked,
                })}
              />
            </div>

            {/* AI OFF toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">AI OFF</Label>
                <p className="text-sm text-muted-foreground">
                  {groupSettings.aiOff ? t('groups.aiOffDesc') : t('groups.aiOnDesc')}
                </p>
              </div>
              <Switch
                checked={groupSettings.aiOff}
                onCheckedChange={(checked) => setGroupSettings({ ...groupSettings, aiOff: checked })}
              />
            </div>

            {groupSettings.isMuted && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <BellOff className="w-4 h-4 inline mr-2" />
                  {t('groups.mutedBanner')}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsModal(false)}>
              {t('groups.cancel')}
            </Button>
            <Button onClick={handleSaveSettings}>
              {t('groups.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
